import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";

const BOOK_CALL_URL = "https://calendly.com/anchorsideglobal-support/30min";

const links = [
  { label: "Services", id: "services" },
  { label: "About", id: "about" },
  { label: "Process", id: "process" },
  { label: "Contact", id: "contact" },
];

export const Navbar = ({ lenis }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      return;
    }
    const el = document.getElementById(id);
    if (!el) return;
    if (lenis?.current) {
      lenis.current.scrollTo(el, { offset: -80, duration: 1.4 });
    } else {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const goHome = () => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      return;
    }
    goTo("hero");
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "bg-parchment/80 backdrop-blur-xl border-b border-petrol/15"
          : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-3 md:py-4 flex items-center justify-between">
        <button onClick={goHome} data-testid="nav-logo-btn" className="cursor-pointer flex flex-col items-start leading-none">
          <Logo />
          <span className="font-serif italic text-verdigris text-base md:text-lg mt-1.5 tracking-tight pl-1">
            Support that holds.
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => goTo(l.id)}
              data-testid={`nav-link-${l.id}`}
              className="nav-link font-sans text-sm uppercase tracking-[0.18em] text-petrol/80 hover:text-petrol transition-colors"
            >
              {l.label}
            </button>
          ))}
          <a
            href={BOOK_CALL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-book-call-btn"
            className="font-sans text-sm uppercase tracking-[0.16em] bg-petrol text-parchment px-7 py-3.5 hover:bg-petrol/90 transition-colors"
          >
            Book a Call
          </a>
        </nav>

        <button
          className="lg:hidden text-petrol"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-parchment border-b border-petrol/15"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <button
                  key={l.id}
                  onClick={() => goTo(l.id)}
                  data-testid={`mobile-nav-link-${l.id}`}
                  className="text-left font-serif text-3xl text-petrol"
                >
                  {l.label}
                </button>
              ))}
              <a
                href={BOOK_CALL_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-book-call-btn"
                className="mt-2 text-center font-sans text-sm uppercase tracking-[0.16em] bg-petrol text-parchment px-7 py-4"
              >
                Book a Call
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
